import type { MessageFieldsFragment } from '#src/graphql/generated';

import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FaCopy, FaFont, FaMicrophone, FaReply } from 'react-icons/fa';
import { Link } from 'react-router-dom';

function getSourceIcon(source: string) {
  switch (source) {
    case 'text':
      return FaFont;
    case 'audio':
      return FaMicrophone;
    default:
      return null;
  }
}

interface MessageCardProps {
  message: MessageFieldsFragment;
  childMessages?: MessageFieldsFragment[];
  parentTargetId: string | null;
  onSetParentTargetId: (id: string | null) => void;
  showReply: boolean;
}

export function MessageCard({
  message,
  childMessages,
  parentTargetId,
  onSetParentTargetId,
  showReply,
}: MessageCardProps) {
  const documentContext = message.messageContexts?.find(
    (ctx) => ctx.name === 'documentId' && ctx.document,
  );
  const document = documentContext?.document;

  return (
    <Box>
      <Box
        p={4}
        bg="bg.subtle"
        borderRadius="md"
        border="1px"
        borderColor="border"
      >
        <HStack justify="space-between" align="start" mb={2}>
          <HStack gap={2} align="center" flexWrap="wrap">
            <Text fontSize="xs" fontWeight="medium" color="fg">
              {message.author?.displayName ?? 'Unknown'}
            </Text>
            <Text fontSize="xs" color="fg.muted">
              ·
            </Text>
            <Text fontSize="xs" color="fg.muted">
              {new Date(message.createdAt).toLocaleString()}
            </Text>
            {getSourceIcon(message.source) && (
              <Box
                as={getSourceIcon(message.source)!}
                aria-label={message.source}
                color="fg.subtle"
                fontSize="xs"
                display="inline-flex"
              />
            )}
            {document && (
              <Link
                to={`/documents/${document.path}`}
                style={{ textDecoration: 'none' }}
              >
                <Badge
                  as="span"
                  colorPalette="blue"
                  variant="subtle"
                  size="sm"
                  cursor="pointer"
                  _hover={{ opacity: 0.9 }}
                >
                  View Document ➡️
                </Badge>
              </Link>
            )}
          </HStack>
          <HStack gap={2} align="center">
            {/* Temporary: copy message ID to clipboard */}
            <IconButton
              size="xs"
              variant="ghost"
              aria-label="Copy message ID"
              onClick={() => void navigator.clipboard.writeText(message.id)}
            >
              <FaCopy />
            </IconButton>
            {showReply &&
              (parentTargetId === message.id ? (
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => onSetParentTargetId(null)}
                >
                  Cancel reply
                </Button>
              ) : (
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => onSetParentTargetId(message.id)}
                >
                  <FaReply />
                  Reply
                </Button>
              ))}
          </HStack>
        </HStack>
        <Text fontSize="sm" whiteSpace="pre-wrap">
          {message.value}
        </Text>
      </Box>
      {childMessages && childMessages.length > 0 && (
        <VStack
          gap={2}
          align="stretch"
          mt={2}
          pl={6}
          borderLeftWidth="2px"
          borderLeftColor="border"
        >
          {childMessages.map((child) => (
            <MessageCard
              key={child.id}
              message={child}
              parentTargetId={parentTargetId}
              onSetParentTargetId={onSetParentTargetId}
              showReply={false}
            />
          ))}
        </VStack>
      )}
    </Box>
  );
}
